/**
 * ElectronicModuleAiLookup — the "✨ AI Lookup" affordance shared by the module
 * picker and the ref editor's legacy-electronics rows. On click it invokes the
 * `suggestElectronicModule` callable with the supplied context (built lazily so
 * it captures fresh state) and shows a small result card:
 *   • `match`      → an existing module + confidence/reasoning + "Link this module"
 *   • `suggestion` → a proposed new module + reasoning + "Create this module"
 *                    (opens the editor modal pre-filled, then links on save)
 *   • neither      → "No suggestion" message
 * All the AI call / loading / error / create-modal state lives here so both call
 * sites just pass a context builder + an `onLink` callback.
 */
import { useState } from 'react';
import {
  suggestElectronicModule,
  type SuggestElectronicModuleInput,
  type SuggestElectronicModuleResult,
  type SuggestElectronicModuleSuggestion,
} from '../../services/firebase';
import { ElectronicModuleEditorModal } from './ElectronicModuleEditorModal';

interface ElectronicModuleAiLookupProps {
  /** Build the callable input at click time (captures the latest ref context). */
  buildInput: () => SuggestElectronicModuleInput;
  /** Link the resolved/created module to the caller (id + best-known name). */
  onLink: (moduleId: string, moduleName: string) => void;
  /** Fallback manufacturer when a `suggestion` carries none (for "Create New"). */
  defaultManufacturerId?: string;
  /** Visual size: 'full' = picker toolbar button, 'mini' = legacy-row button. */
  variant?: 'full' | 'mini';
  disabled?: boolean;
}

function ConfidenceBadge({ confidence }: { confidence: 'high' | 'medium' | 'low' }) {
  const styles =
    confidence === 'high'
      ? 'bg-emerald-100 text-emerald-700'
      : confidence === 'medium'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-gray-100 text-gray-600';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${styles}`}
    >
      {confidence} confidence
    </span>
  );
}

export function ElectronicModuleAiLookup({
  buildInput,
  onLink,
  defaultManufacturerId,
  variant = 'full',
  disabled = false,
}: ElectronicModuleAiLookupProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SuggestElectronicModuleResult | null>(
    null,
  );
  // Suggestion currently seeding the "Create New" editor modal.
  const [createFrom, setCreateFrom] =
    useState<SuggestElectronicModuleSuggestion | null>(null);

  const run = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await suggestElectronicModule(buildInput());
      setResult(res);
    } catch (err) {
      // Surface the HttpsError.message verbatim.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setError(null);
    setResult(null);
  };

  const handleCreated = (savedId: string) => {
    const name = createFrom?.name || '';
    // Close the suggestion card and link the module to the ref, but keep the
    // editor open (don't clear `createFrom`) so the auto-fired enrichment review
    // stays on screen. The editor's own onClose clears `createFrom` when the
    // user is done.
    close();
    onLink(savedId, name);
  };

  const triggerClass =
    variant === 'mini'
      ? 'rounded border border-brand-200 px-2 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40'
      : 'text-xs font-medium px-2 py-1 rounded border border-brand-300 text-brand-700 hover:bg-brand-50 disabled:opacity-50';

  return (
    <>
      <button
        type="button"
        onClick={run}
        disabled={disabled || loading}
        title="AI Lookup — find or propose a matching module"
        className={triggerClass}
      >
        {variant === 'mini' ? '✨ AI' : '✨ AI Lookup'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={close}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="font-medium text-gray-900">✨ AI Module Lookup</h3>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="px-4 py-4">
              {loading && (
                <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-300 border-t-brand-600" />
                  Asking the model…
                </div>
              )}

              {!loading && error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              {!loading && !error && result && (
                <>
                  {result.match ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                        Existing module match
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {result.match.moduleName}
                        </span>
                        <ConfidenceBadge confidence={result.match.confidence} />
                      </div>
                      <p className="text-sm text-gray-600">
                        {result.match.reasoning}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          const m = result.match!;
                          close();
                          onLink(m.moduleId, m.moduleName);
                        }}
                        className="mt-1 rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
                      >
                        Link this module
                      </button>
                    </div>
                  ) : result.suggestion ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                        Proposed new module
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {result.suggestion.name}
                        </span>
                        <ConfidenceBadge
                          confidence={result.suggestion.confidence}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {result.suggestion.reasoning}
                      </p>
                      <button
                        type="button"
                        onClick={() => setCreateFrom(result.suggestion!)}
                        className="mt-1 rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
                      >
                        Create this module
                      </button>
                    </div>
                  ) : (
                    <p className="py-2 text-sm text-gray-500">
                      No suggestion — the model couldn’t match or propose a
                      module from this context.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ElectronicModuleEditorModal
        isOpen={!!createFrom}
        onClose={() => setCreateFrom(null)}
        module={null}
        defaultManufacturerId={
          createFrom?.manufacturerId || defaultManufacturerId
        }
        defaultName={createFrom?.name}
        onSaved={handleCreated}
        autoEnrichOnSave
      />
    </>
  );
}
